import {css, StyleSheet} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {
    withActionScheduler,
    type WithActionSchedulerProps,
    type WithoutActionScheduler,
} from "@khanacademy/wonder-blocks-timing";
import {color, spacing} from "@khanacademy/wonder-blocks-tokens";

import AphroditeCSSTransitionGroup from "#shared-styles-package/aphrodite-css-transition-group.tsx";

import OfficialClarificationsQuery from "./official-clarifications-query.tsx";
import type {OfficialClarification} from "./types.ts";

type VideoPlayerModel =
    typeof import("../video-package/video-player-model.ts").default;

type Props = {
    videoModel: VideoPlayerModel;
    cached: boolean;
} & WithActionSchedulerProps;

/**
 * Notifications that pop up over the playing video.
 *
 * Every half second this checks to see which clarifications should be visible
 * at that moment. If there are none this widget is hidden.
 */
class ClarificationNotifications extends React.Component<Props> {
    componentDidMount() {
        const {schedule} = this.props;

        // Check every 1/2 second where the video is at and show the
        // appropriate tooltip(s)
        // (sadly there is no way to hook into yt seek events -
        // https://code.google.com/p/gdata-issues/issues/detail?id=2915)
        schedule.interval(() => this.forceUpdate(), 500);
    }

    renderClarifications(
        clarifications: Array<OfficialClarification>,
    ): React.ReactElement<React.ComponentProps<typeof View>> | null {
        const {videoModel} = this.props;

        const time = videoModel.getVideoPosition();

        const clarificationsNow = clarifications.filter(
            (c) => time >= c.openTimestamp && time <= c.closeTimestamp,
        );

        if (clarificationsNow.length === 0) {
            return null;
        }

        return (
            <View style={styles.clarification}>
                {clarificationsNow.map((c, i) => {
                    return (
                        <View key={c.id}>
                            {/* NOTE: c.text may contain HTML */}
                            <span dangerouslySetInnerHTML={{__html: c.text}} />
                            {i < clarificationsNow.length - 1 && (
                                <hr className={css(styles.splitter)} />
                            )}
                        </View>
                    );
                })}
            </View>
        );
    }

    render(): React.ReactNode {
        const {cached, videoModel} = this.props;

        const youtubeId = videoModel.get("translatedYoutubeId");
        return (
            <OfficialClarificationsQuery
                cacheWithoutReporters={cached}
                youtubeId={youtubeId}
            >
                {({data, loading}) => {
                    if (
                        loading ||
                        !data ||
                        !data.officialClarifications ||
                        data.officialClarifications.length === 0
                    ) {
                        return null;
                    }

                    const clarifications = this.renderClarifications(
                        data.officialClarifications,
                    );

                    return (
                        <AphroditeCSSTransitionGroup
                            transitionStyle={{
                                enter: styles.popupEnter,
                                enterActive: styles.popupEnterActive,
                                leave: styles.popupLeave,
                                leaveActive: styles.popupLeaveActive,
                            }}
                            transitionEnterTimeout={400}
                            transitionLeaveTimeout={400}
                        >
                            {clarifications}
                        </AphroditeCSSTransitionGroup>
                    );
                }}
            </OfficialClarificationsQuery>
        );
    }
}

const styles = StyleSheet.create({
    clarification: {
        position: "absolute",
        bottom: spacing.xxLarge_48,
        right: spacing.xxSmall_6,
        padding: spacing.small_12,
        maxWidth: 400,
        backgroundColor: color.offBlack64,
        border: `1px solid ${color.white}`,
        color: color.white,
    },
    splitter: {
        margin: 0,
    },
    popupEnter: {
        transition: "400ms cubic-bezier(0.250, 0.250, 0.750, 0.750) all",
        opacity: 0,
    },
    popupEnterActive: {
        opacity: 1,
    },
    popupLeave: {
        transition: "400ms cubic-bezier(0.250, 0.250, 0.750, 0.750) all",
        opacity: 1,
    },
    popupLeaveActive: {
        opacity: 0,
    },
});

type ExportProps = WithoutActionScheduler<
    JSX.LibraryManagedAttributes<
        typeof ClarificationNotifications,
        React.ComponentProps<typeof ClarificationNotifications>
    >
>;

export default withActionScheduler(
    ClarificationNotifications,
) as React.ComponentType<ExportProps>;
